import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  AttachMoney,
  ShoppingCart,
  Home
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '../../hooks/useApi';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';

interface MarketGrouping {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  market_count: number;
  visible_user_count: number;
  product_count: number;
  created_at: string;
  updated_at: string;
}

export const MarketGroupingsAdmin: React.FC = () => {
  const { data: user, isLoading: userLoading } = useCurrentUser();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newGroupingName, setNewGroupingName] = useState('');
  const [newGroupingDescription, setNewGroupingDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch all groupings from private-db
  const { data: groupingsData, isLoading: groupingsLoading } = useQuery({
    queryKey: ['customization-groupings-admin'],
    queryFn: async () => {
      const response = await authenticatedFetch(
        '/api/marketcompass/admin/customization-groupings/list'
      );
      if (!response) throw new Error('No response from server');
      if (!response.ok) {
        throw new Error('Failed to fetch groupings');
      }
      return response.json();
    },
    enabled: !!user
  });

  // Create grouping mutation
  const createMutation = useMutation({
    mutationFn: async (data: { name: string; description: string }) => {
      const response = await authenticatedFetch(
        '/api/marketcompass/admin/customization-groupings',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        }
      );
      if (!response) throw new Error('No response from server');
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create grouping');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customization-groupings-admin'] });
      setCreateDialogOpen(false);
      setNewGroupingName('');
      setNewGroupingDescription('');
      setSuccess('Market grouping created successfully');
      setError(null);
      setTimeout(() => setSuccess(null), 5000);
    },
    onError: (error: Error) => {
      setError(error.message);
      setTimeout(() => setError(null), 5000);
    }
  });

  // Delete grouping mutation
  const deleteMutation = useMutation({
    mutationFn: async (groupingId: number) => {
      const response = await authenticatedFetch(
        `/api/marketcompass/admin/customization-groupings/${groupingId}/delete`,
        { method: 'DELETE' }
      );
      if (!response) throw new Error('No response from server');
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete grouping');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customization-groupings-admin'] });
      setSuccess('Market grouping deleted successfully');
      setError(null);
      setTimeout(() => setSuccess(null), 5000);
    },
    onError: (error: Error) => {
      setError(error.message);
      setTimeout(() => setError(null), 5000);
    }
  });

  const handleCreateGrouping = () => {
    if (!newGroupingName.trim()) {
      setError('Grouping name is required');
      return;
    }
    createMutation.mutate({
      name: newGroupingName.trim(),
      description: newGroupingDescription.trim()
    });
  };

  const handleDeleteGrouping = (groupingId: number) => {
    if (window.confirm('Are you sure you want to delete this grouping? This action cannot be undone.')) {
      deleteMutation.mutate(groupingId);
    }
  };

  // Show loading spinner while user data is being fetched
  if (userLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have admin permission
  if (!user?.permissions?.includes('market_compass.admin')) {
    return <Navigate to="/" replace />;
  }

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Grouping Name',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1.5,
      minWidth: 250
    },
    {
      field: 'market_count',
      headerName: 'Markets',
      width: 100,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'visible_user_count',
      headerName: 'Visible Users',
      width: 120,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'product_count',
      headerName: 'Products',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          color={params.value > 0 ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="Edit Grouping">
            <IconButton
              size="small"
              onClick={() => navigate(`/admin/market-groupings/${params.row.id}`)}
              color="primary"
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Grouping">
            <IconButton
              size="small"
              onClick={() => handleDeleteGrouping(params.row.id)}
              color="error"
              disabled={params.row.product_count > 0}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const groupings: MarketGrouping[] = groupingsData?.groupings || [];

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            color="inherit"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary">Market Groupings</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Typography variant="h4" component="h1" gutterBottom>
              Market Groupings Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create and manage market grouping packages with custom pricing
            </Typography>
          </div>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Grouping
          </Button>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Data Grid */}
        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={groupings}
            columns={columns}
            loading={groupingsLoading}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'created_at', sort: 'desc' }] }
            }}
            disableRowSelectionOnClick
            onRowClick={(params) => navigate(`/admin/market-groupings/${params.row.id}`)}
            sx={{ cursor: 'pointer' }}
          />
        </Paper>

        {/* Create Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Create Market Grouping</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Grouping Name"
              type="text"
              fullWidth
              value={newGroupingName}
              onChange={(e) => setNewGroupingName(e.target.value)}
              required
              sx={{ mb: 2, mt: 1 }}
            />
            <TextField
              margin="dense"
              label="Description"
              type="text"
              fullWidth
              multiline
              rows={3}
              value={newGroupingDescription}
              onChange={(e) => setNewGroupingDescription(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreateGrouping}
              variant="contained"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? <CircularProgress size={24} /> : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};
